// API 서비스
import { API_BASE_URL } from '../config/api';
import { getToken, setToken, removeToken } from '../shared/auth';

// Re-export for convenience (다른 서비스에서 import 편의를 위해)
export { API_BASE_URL };

// 인증 모듈에서 토큰 관리 함수 re-export
export { getToken, setToken, removeToken };

// ApiError 클래스
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Auth 헤더 생성
export const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// API 서비스 객체 (fetch wrapper)
const api = {
  get: async (endpoint) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return { data };
  },

  post: async (endpoint, body, config = {}) => {
    const token = getToken();
    const isFormData = body instanceof FormData;

    const headers = {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      ...(config.headers || {})
    };

    const fetchOptions = {
      method: 'POST',
      headers,
      body: isFormData ? body : JSON.stringify(body)
    };

    // Handle timeout if specified
    let response;
    const timeout = config.timeout || (isFormData ? 120000 : 30000); // FormData는 2분, 일반은 30초
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...fetchOptions,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('요청 시간이 초과되었습니다. 네트워크 연결을 확인해주세요.');
      }
      // iOS Safari "Load failed" 등 네트워크 오류 처리
      if (error.message === 'Load failed' || error.message === 'Failed to fetch' || error.message === 'NetworkError') {
        throw new Error('네트워크 연결 실패. WiFi 또는 데이터 연결을 확인해주세요.');
      }
      throw new Error(`네트워크 오류: ${error.message}`);
    }

    try {
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Request failed');
      return { data };
    } catch (parseError) {
      if (!response.ok) {
        throw new Error(`서버 오류 (${response.status})`);
      }
      throw parseError;
    }
  },

  put: async (endpoint, body) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return { data };
  },

  delete: async (endpoint) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return { data };
  }
};

export default api;
